import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

const filterStateStore: Record<string, any> = {}

export interface FilterConfig {
  [key: string]: {
    type: 'string' | 'number' | 'array' | 'date'
    coreState?: any
    coreSetter?: (value: any) => void
    default?: any
  }
}

export function useFilters(filterConfig: FilterConfig, routePrefix: string) {
  const history = useHistory()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)

  const isFilterRoute = useMemo(() => {
    return location.pathname.includes(routePrefix)
  }, [location.pathname, routePrefix])

  const urlFilters = useMemo(() => {
    const filters: Record<string, any> = {}
    Object.keys(filterConfig).forEach(key => {
      const config = filterConfig[key]
      const param = searchParams.get(key)
      if (param) {
        switch (config.type) {
          case 'number':
            filters[key] = Number(param)
            break
          case 'array':
            // Support string IDs for carrierIds; parse as string array
            if (key === 'carrierIds') {
              filters[key] = param.split(',')
            } else {
              filters[key] = param.split(',').map(Number)
            }
            break
          default:
            filters[key] = param
        }
      }
    })
    return filters
  }, [searchParams, filterConfig])

  // Sync core state with URL parameters when navigating to a filter route
  useEffect(() => {
    if (isFilterRoute) {
      // Sync each URL filter value to core state
      Object.entries(urlFilters).forEach(([key, value]) => {
        const config = filterConfig[key]
        if (config?.coreSetter && value !== undefined) {
          // Only update core state if the URL value is different from current core state
          if (JSON.stringify(config.coreState) !== JSON.stringify(value)) {
            config.coreSetter(value)
          }
        }
      })

      // Also sync missing filters (those not in URL) to their default values
      Object.keys(filterConfig).forEach(key => {
        if (urlFilters[key] === undefined) {
          const config = filterConfig[key]
          if (config?.coreSetter) {
            const currentValue = config.coreState

            // Don't reset fields that have valid non-empty default values
            // searchType has a default of 'reservationNumber' which should be preserved
            if (key === 'searchType' && currentValue && currentValue !== 'all') {
              return
            }

            // search field should be empty, not 'all'
            if (key === 'search' && currentValue) {
              config.coreSetter('')
              return
            }

            // Reset to default if not in URL and current value is not already default
            if (typeof currentValue === 'string' && currentValue !== '' && currentValue !== 'all') {
              config.coreSetter('all')
            } else if (typeof currentValue === 'number' && currentValue !== 0 && currentValue !== undefined) {
              config.coreSetter(undefined)
            } else if (Array.isArray(currentValue) && currentValue.length > 0) {
              config.coreSetter([])
            }
          }
        }
      })
    }
  }, [isFilterRoute, location.pathname, location.search]) // Re-run when route or search params change

  const getFilterValue = useCallback(
    (key: string) => {
      const config = filterConfig[key]
      if (isFilterRoute && urlFilters[key] !== undefined) {
        return urlFilters[key]
      }
      return config?.coreState
    },
    [isFilterRoute, urlFilters, filterConfig],
  )

  const filterValues: Record<string, any> = {}
  Object.keys(filterConfig).forEach(key => {
    filterValues[key] = getFilterValue(key)
  })

  const updateUrlParams = useCallback(
    (updates: Record<string, any>) => {
      if (!isFilterRoute) return

      const currentParams = new URLSearchParams(location.search)
      const newParams: Record<string, string> = {}

      currentParams.forEach((value, key) => {
        newParams[key] = value
      })

      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
          delete newParams[key]
        } else if (Array.isArray(value)) {
          newParams[key] = value.join(',')
        } else {
          newParams[key] = String(value)
        }
      })

      const queryString = new URLSearchParams(newParams).toString()
      const newUrl = `${location.pathname}${queryString ? `?${queryString}` : ''}`

      if (location.pathname + location.search !== newUrl) {
        history.replace(newUrl)
      }
    },
    [isFilterRoute, location.pathname, location.search, history],
  )

  const setFilter = useCallback(
    (key: string, value: any) => {
      const config = filterConfig[key]
      console.log({ isFilterRoute, key, value })
      if (config?.coreSetter) {
        config.coreSetter(value)
      }
      if (isFilterRoute) {
        updateUrlParams({ [key]: value })
      }
    },
    [filterConfig, isFilterRoute, updateUrlParams],
  )

  const resetFilters = useCallback(() => {
    Object.keys(filterConfig).forEach(key => {
      const config = filterConfig[key]
      if (config.coreSetter) {
        // Reset to appropriate defaults based on the field
        if (key === 'search' && !config.default) {
          // search should be empty string, not 'all'
          config.coreSetter('')
        } else if (typeof config.coreState === 'string' && config.coreState !== '' && !config.default) {
          // Other string fields like carrierId, countryId etc. can be 'all'
          config.coreSetter('all')
        } else {
          config.coreSetter(config.default || undefined)
        }
      }
    })

    if (isFilterRoute) {
      history.replace(location.pathname)
    }
  }, [filterConfig, isFilterRoute, history, location.pathname])

  return {
    values: filterValues,
    setFilter,
    updateUrlParams,
    resetFilters,
    isFilterRoute,
  }
}
